package com.eladfinish.bible.contest.utils

import com.eladfinish.bible.contest.constants.StringConstants
import com.eladfinish.bible.contest.constants.StringConstants.COLON_SPLITTER
import com.eladfinish.bible.contest.constants.StringConstants.MAKAF
import com.eladfinish.bible.contest.constants.StringConstants.Regex.ALEF_TAV
import com.eladfinish.bible.contest.constants.StringConstants.Regex.ALEF_TAV_
import com.eladfinish.bible.contest.extensions.replaceRegex

object MyStringUtils {

    const val qMark = "?" // question mark for optional complete vav or yud

    fun retrieveVerse(list: List<String>): String =
        list.toString()
            .unpartition()

    private fun String.unpartition(): String =
        this.replace(
            regex = "[\\[\\],]".toRegex(),
            replacement = ""
        ).replace(
            regex = "$MAKAF ".toRegex(),
            replacement = MAKAF + ""
        )

    fun getRegexOfNikudOnLetters(str: String): String {
        val ot = "[.$ALEF_TAV_]" // ot is an Hebrew letter or a dot
        val notAlefTavSpace = "[^א-ת$MAKAF$COLON_SPLITTER\\s]"
        var result = str.replaceRegex(regex = "($ot(?:${getSiman()}\\??)*)", replacement = "(?:$1@)")
        result = result.replace(oldValue = "@", newValue = "$notAlefTavSpace*").trim() // @ for non-letter char.
        return result
    }

    //this method doesn't catch the first ot pasuk (since it's not prefixed by a colonSplitter)
    // NOTE: applied via appendNbspAfterOtiotPasuk() — on iOS a look-behind-free scanner replaces
    // this regex (see OtiotPasukPlatform.kt); this pattern is the Android implementation and the
    // behavioral reference.
    //
    // `\b` is deliberately NOT used around the letters group: on desktop (plain OpenJDK
    // java.util.regex), `\b`'s word-boundary test never fires between a Hebrew letter and plain
    // whitespace (only between a letter and a nikud mark), so both the leading and trailing `\b`
    // silently failed to match ANY ot-pasuk letter on desktop — see getRegexOfOtiotPasuk() below
    // for the full explanation. The look-behind already guarantees whitespace precedes the group,
    // and the literal `\s` that follows it already guarantees the group ends exactly where the
    // letters end, so `\b` added nothing but platform-inconsistent breakage.
    internal fun getRegexOfOtiotPasukPlainSpace(): String {
        return "(?<=(?:\\]|פרק|$COLON_SPLITTER)\\s)($ALEF_TAV{1,3})(\\s)"
    }

    //NO-BREAK SPACE (NON-BREAKING SPACE) //not breaking space, no break, NON BREAKING, NonBreakingSpace
    fun String.replaceSpaceWithNoBreakSpace(): String =
        this //"\u00A0" (160 in decimal) is NO-BREAK SPACE
            .replaceRegex(
                regex = "(פרק)" + ' ',
                replacement = "$1" + StringConstants.NO_BREAK_SPACE
            ) // make "perek" and its letters at same line
            .replaceRegex(
                regex = ' '.toString() + "(׀)",
                replacement = StringConstants.NO_BREAK_SPACE + "$1"
            ) // avoid line starting with pasek
            .appendNbspAfterOtiotPasuk() // avoid line breaking after ot pasuk

    // 1425-1479 is the unicode range of nikud and teamim
    private const val NIKUD_TEAMIM_START = 1425
    private const val NIKUD_TEAMIM_END = 1479

    // 1488-1514 is the unicode range of Hebrew letters
    private const val ALEF_TAV_START = 0x05D0 // א
    private const val ALEF_TAV_END = 0x05EA // ת

    // Builds a character-class body for [start..end] with the given code points carved out as
    // separate sub-ranges. Java/ICU's regex engines support excluding characters from a range via
    // intersection syntax ("[range&&[^x]]"), but that's not valid ECMAScript regex at all (native
    // JS RegExp throws a SyntaxError on it) — this is the portable equivalent.
    private fun rangeExcluding(start: Int, end: Int, vararg exclude: Int): String {
        val excluded = exclude.toSet()
        val sb = StringBuilder()
        var rangeStart = start
        for (codePoint in start..end) {
            if (codePoint in excluded) {
                if (rangeStart < codePoint) sb.appendRange(rangeStart, codePoint - 1)
                rangeStart = codePoint + 1
            }
        }
        if (rangeStart <= end) sb.appendRange(rangeStart, end)
        return sb.toString()
    }

    private fun StringBuilder.appendRange(from: Int, to: Int) {
        if (from == to) append(from.toChar()) else append(from.toChar()).append('-').append(to.toChar())
    }

    fun getSiman(): String =
        "[@" + rangeExcluding(NIKUD_TEAMIM_START, NIKUD_TEAMIM_END, MAKAF.code) + "]"

    fun isFromSifreyEmet(bookNameHebrew: String): Boolean = bookNameHebrew in StringConstants.SIFREY_EMET

    fun getSimanNoDagesh(): String =
        "[@" + rangeExcluding(NIKUD_TEAMIM_START, NIKUD_TEAMIM_END, MAKAF.code, StringConstants.DAGESH.code) + "]"

    // TODO: 03/01/2017 חיפוש אות עם סימן רפה = ללא דגש
    fun getSimanNoRafe(): String =
        "[@" + rangeExcluding(NIKUD_TEAMIM_START, NIKUD_TEAMIM_END, MAKAF.code, StringConstants.RAFE.code) + "]"

    //exclude letter
    fun getAlefTavExcluding(vararg chars: Char): String =
        "[" + rangeExcluding(ALEF_TAV_START, ALEF_TAV_END, *chars.map { it.code }.toIntArray()) + "]"

    fun getSeyfaAndColon(seyfa: String?, newline: Boolean): CharSequence =
        seyfa + COLON_SPLITTER + (if (newline) "\n" else " ")

    fun addParenthesisOnOtiotPasuk(input: String): String =
        input
            .replaceRegex(
                regex = getRegexOfSingleOtPasukAleph(),
                replacement = "($1)"
            )
            .parenthesizeOtiotPasuk()
}
